import { Dialog, Loader, useDialog } from 'components';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import SuccessDialog from 'components/organization/dialog/success-dialog';
import { useAuth } from 'contexts';
import { useResponse } from 'contexts/response';
import { useCarrierAPI } from 'hooks/use-carrier-api';
import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getTruckingCompanyById } from 'services';
import { CarrierContact, Role, TruckingCompany } from 'types';

const Contact: FC<CarrierContact> = ({ location, email }) => (
  <div className="grid-4 p-2 w-100">
    <div>
      <h6 className="text-dark font-weight-regular">Location</h6>
      <p className="text-black">{!!location ? `${location.name} (${location.code})` : ''}</p>
    </div>
    <div>
      <h6 className="text-dark font-weight-regular">Email(s)</h6>
      <p className="text-black">{email}</p>
    </div>
  </div>
);

export const ViewCarrier: FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { activateCarrier, deactivateCarrier } = useCarrierAPI();
  const { closeDialog, openDialog, ref } = useDialog();
  const { userProfile } = useAuth();

  const [loading, setLoading] = useState<boolean>(false);
  const { error, setError } = useResponse();

  const [carrier, setCarrier] = useState<TruckingCompany | null>(null);

  useEffect(() => {
    if (!!params.id && !isNaN(Number(params.id))) {
      getTruckingCompanyById(Number(params.id)).then(setCarrier);
    }
  }, [params.id]);

  const handleCloseDialog = useCallback(async () => {
    if (!loading) {
      closeDialog();
      setError('');
    }
  }, [loading, closeDialog, setError]);

  const handleStatusChange = useCallback(async () => {
    if (!!carrier) {
      setLoading(true);
      const response = carrier.deleted ? await activateCarrier(carrier.id) : await deactivateCarrier(carrier.id);
      setCarrier(response);
      if (!!response) {
        await handleCloseDialog();
      }
      setLoading(false);
    }
  }, [carrier, activateCarrier, deactivateCarrier, handleCloseDialog]);

  return (
    <>
      <Loader loading={!carrier}>
        {carrier && (
          <div className="page" id="carrier-details">
            <div className="page-header">
              <h1>CARRIER {carrier.name && carrier.scacCode ? `${carrier.name} (${carrier.scacCode})` : 'N/A'}</h1>
              <div className="d-flex g-3 flex-row-reverse">
                {[Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING, Role.ADMIN_CUSTOMER].includes(
                  userProfile.role
                ) && (
                  <button
                    className="btn-primary text-uppercase"
                    onClick={() => navigate('edit', { replace: true })}
                    data-cy="carrier-edit-button"
                  >
                    Edit
                  </button>
                )}
                {[Role.ADMIN, Role.BILLING_ADMIN].includes(userProfile.role) &&
                  (carrier.deleted ? (
                    <button
                      className="btn-outline text-uppercase"
                      onClick={openDialog}
                      data-cy="carrier-activate-button"
                    >
                      Activate
                    </button>
                  ) : (
                    <button
                      className="btn-danger text-uppercase"
                      onClick={openDialog}
                      data-cy="carrier-deactivate-button"
                    >
                      Deactivate
                    </button>
                  ))}
                <button className="btn-back" onClick={() => navigate('/scac-management', { replace: true })}>
                  <span className="material-icons">arrow_back_ios</span>
                  BACK
                </button>
              </div>
            </div>
            <div className="card-primary">
              <div className="card-header px-3">
                <h4 className="text-uppercase">Carrier Information</h4>
              </div>
              <div className="card-body">
                <div className="grid-4 p-2">
                  <div>
                    <h6 className="text-dark font-weight-regular">Name</h6>
                    <p className="text-black">{carrier.name}</p>
                  </div>
                  <div>
                    <h6 className="text-dark font-weight-regular">SCAC Code</h6>
                    <p className="text-black">{carrier.scacCode}</p>
                  </div>
                  <div>
                    <h6 className="text-dark font-weight-regular">Status</h6>
                    <p className="text-black">{carrier.deleted ? 'Deactivated' : 'Active'}</p>
                  </div>
                  <div>
                    <h6 className="text-dark font-weight-regular">USDOT Number</h6>
                    <p className="text-black">{carrier.usdotNumber ?? ''}</p>
                  </div>
                </div>
                <div className="grid-2 p-2">
                  <div>
                    <h6 className="text-dark font-weight-regular">Notes</h6>
                    <p className="text-black" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {carrier.notes ?? ''}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid-auto">
              <div className="card-primary">
                <div className="card-header px-3">
                  <h4 className="text-uppercase">contact(s)</h4>
                </div>
                <div className="card-body">
                  <div className="d-flex flex-column align-items-start">
                    {!!carrier.contacts && carrier.contacts.length > 0 ? (
                      carrier.contacts.map((contact, index) => <Contact key={index} {...contact} />)
                    ) : (
                      <p>None</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Loader>
      <Dialog closeDialog={handleCloseDialog} ref={ref} data-cy="carrier-dialog">
        {!error ? (
          <Loader loading={loading}>
            <div className="d-flex flex-column g-3">
              <h3 className="text-info-dark font-weight-medium">{`${
                carrier?.deleted ? 'Activate' : 'Deactivate'
              } Carrier`}</h3>
              <p className="text-info-main">{`${carrier?.deleted ? 'Activate' : 'Deactivate'} carrier ${
                carrier?.name
              } (${carrier?.scacCode})?`}</p>
              <div className="grid-3 mt-3">
                <button className="btn-outline col-start-2" onClick={closeDialog}>
                  close
                </button>
                <button className="btn-danger col-start-3" onClick={handleStatusChange} data-cy="confirm-button">
                  confirm
                </button>
              </div>
            </div>
          </Loader>
        ) : (
          <div className="d-flex flex-column g-3">
            <h3 className="text-info-dark font-weight-medium">Updating Carrier Status Failed</h3>
            <p className="text-info-main">{error}</p>
            <div className="grid-3 mt-3">
              <button className="btn-outline col-start-3" onClick={handleCloseDialog}>
                close
              </button>
            </div>
          </div>
        )}
      </Dialog>
      <ErrorDialog title="Save Carrier Failed" />
      <SuccessDialog title="Carrier Saved" />
    </>
  );
};
